<script setup>
import { computed, onMounted, ref } from 'vue'
import {
  createHealthCoverageLink,
  deleteHealthCoverageLink,
  getHealthCoverageLinks,
  updateHealthCoverageLink
} from '../../api/reportApi.js'
import AppAlert from '../../shared/components/AppAlert.vue'
import AppButton from '../../shared/components/AppButton.vue'
import {
  RELATION_TYPES,
  groupCoverageOptions,
  groupLinksByArea,
  isLinkChanged,
  toLinkRequest,
  validateLinkForm
} from './healthCoverageLinkUtils.js'

const settings = ref({ links: [], areas: [], coverages: [], mappingVersion: '' })
const drafts = ref({})
const form = ref(emptyForm())
const isLoading = ref(true)
const savingId = ref(null)
const error = ref('')
const formError = ref('')

const groups = computed(() => groupLinksByArea(settings.value.links, settings.value.areas))
const coverageGroups = computed(() => groupCoverageOptions(settings.value.coverages))

function emptyForm() {
  return { areaCode: '', coverageCode: '', relationType: 'RELATED', sortOrder: '' }
}

function apply(next) {
  settings.value = { links: [], areas: [], coverages: [], mappingVersion: '', ...next }
  drafts.value = Object.fromEntries(settings.value.links.map((link) => [link.id, {
    relationType: link.relationType, sortOrder: link.sortOrder, enabled: link.enabled
  }]))
}

async function load() {
  isLoading.value = true
  error.value = ''
  try {
    apply(await getHealthCoverageLinks())
  } catch (cause) {
    error.value = cause.message || '건강 영역-보장 연결을 불러오지 못했습니다.'
  } finally {
    isLoading.value = false
  }
}

async function run(id, action) {
  savingId.value = id
  error.value = ''
  try {
    apply(await action())
    return true
  } catch (cause) {
    error.value = cause.message || '저장하지 못했습니다.'
    return false
  } finally {
    savingId.value = null
  }
}

async function addLink() {
  formError.value = validateLinkForm(form.value, settings.value.links)
  if (formError.value) return
  if (await run('new', () => createHealthCoverageLink(toLinkRequest(form.value)))) {
    form.value = { ...emptyForm(), areaCode: form.value.areaCode }
  }
}

function saveLink(link) {
  const draft = drafts.value[link.id]
  const message = validateLinkForm({ ...link, ...draft }, settings.value.links, link.id)
  if (message) {
    error.value = message
    return
  }
  run(link.id, () => updateHealthCoverageLink(link.id, toLinkRequest({ ...link, ...draft })))
}

function removeLink(link) {
  if (!window.confirm(`${link.areaLabel} 영역에서 '${link.coverageName}' 연결을 삭제할까요?`)) return
  run(link.id, () => deleteHealthCoverageLink(link.id))
}

onMounted(load)
</script>

<template>
  <section class="link-settings" aria-labelledby="link-settings-title">
    <div class="history-heading">
      <div>
        <p class="home-kicker">관리자 설정</p>
        <h1 id="link-settings-title">건강 영역 · 보장 연결</h1>
        <p class="page-lead">검진 결과의 각 영역을 리포트에서 함께 보여 줄 보장과 연결합니다. 바꾼 내용은 새로 생성하는 리포트부터 적용되고, 이미 만든 리포트는 그대로입니다.</p>
      </div>
      <p v-if="settings.mappingVersion" class="link-settings-version">기준 버전 <b>{{ settings.mappingVersion }}</b></p>
    </div>

    <AppAlert v-if="error">{{ error }}</AppAlert>

    <form class="link-add" @submit.prevent="addLink">
      <label>
        <span>건강 영역</span>
        <select v-model="form.areaCode" :disabled="isLoading">
          <option value="">선택</option>
          <option v-for="area in settings.areas" :key="area.code" :value="area.code">{{ area.label }}</option>
        </select>
      </label>
      <label class="link-add-coverage">
        <span>보장</span>
        <select v-model="form.coverageCode" :disabled="isLoading">
          <option value="">선택</option>
          <optgroup v-for="group in coverageGroups" :key="group.label" :label="group.label">
            <option v-for="coverage in group.options" :key="coverage.code" :value="coverage.code">{{ coverage.label }}</option>
          </optgroup>
        </select>
      </label>
      <label>
        <span>연결 종류</span>
        <select v-model="form.relationType" :disabled="isLoading">
          <option v-for="type in RELATION_TYPES" :key="type.value" :value="type.value">{{ type.label }}</option>
        </select>
      </label>
      <label class="link-add-order">
        <span>순서</span>
        <input v-model="form.sortOrder" type="number" min="0" step="1" placeholder="자동" :disabled="isLoading">
      </label>
      <AppButton type="submit" size="sm" :disabled="isLoading || savingId === 'new'">연결 추가</AppButton>
      <p v-if="formError" class="history-row-error link-add-error">{{ formError }}</p>
    </form>

    <div class="history-table-wrap">
      <table class="history-table link-table">
        <thead>
          <tr>
            <th scope="col">건강 영역</th>
            <th scope="col">보장</th>
            <th scope="col">연결 종류</th>
            <th scope="col">순서</th>
            <th scope="col">사용</th>
            <th scope="col">관리</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading"><td colspan="6" class="history-empty">불러오는 중입니다.</td></tr>
          <tr v-else-if="!groups.length"><td colspan="6" class="history-empty">연결된 보장이 없습니다.</td></tr>
          <template v-for="group in groups" v-else :key="group.areaCode">
            <tr v-for="(link, index) in group.links" :key="link.id" :class="{ 'is-disabled': !drafts[link.id]?.enabled }">
              <th v-if="index === 0" scope="rowgroup" :rowspan="group.links.length" class="link-area">{{ group.areaLabel }}</th>
              <td>
                <b>{{ link.coverageName }}</b>
                <small>{{ link.coverageCategory }}</small>
              </td>
              <td>
                <select v-model="drafts[link.id].relationType" :aria-label="`${link.coverageName} 연결 종류`">
                  <option v-for="type in RELATION_TYPES" :key="type.value" :value="type.value">{{ type.label }}</option>
                </select>
              </td>
              <td><input v-model.number="drafts[link.id].sortOrder" type="number" min="0" step="1" :aria-label="`${link.coverageName} 순서`"></td>
              <td><input v-model="drafts[link.id].enabled" type="checkbox" :aria-label="`${link.coverageName} 사용`"></td>
              <td class="link-actions"><div>
                <AppButton size="sm" variant="secondary" :disabled="savingId !== null || !isLinkChanged(link, drafts[link.id])" @click="saveLink(link)">저장</AppButton>
                <AppButton size="sm" variant="danger" :disabled="savingId !== null" @click="removeLink(link)">삭제</AppButton>
              </div></td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </section>
</template>
